import { Loader2, RotateCcw } from "lucide-react";

import type { UserType } from "@/types/user";

import { usePrivateAxios } from "@/api/use-private-axios";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogTitle,
	DialogTrigger
} from "@/components/ui/dialog";
import useAuthStore from "@/store/auth";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { SidebarMenuButton } from "../ui/sidebar";

type CONSTANTS = Record<UserType, {
	title: string;
	description: string;
}>;
const SWITCHER_CONSTANTS: CONSTANTS = {
	VENDOR: {
		title: "Switch to Buyer",
		description: "Switch to the Buyer account to purchase products from vendors. Your existing details will be used to generate an account for you"
	},
	BUYER: {
		title: "Switch to Vendor",
		description: "With the Vendor account, you can sell and receive payment for your products. Your existing details will be used to generate an account for you"
	}

};

export function AppSwitcher() {
	const navigate = useNavigate();
	const user = useAuthStore(state => state.user);
	const updateUser = useAuthStore(state => state.updateUser);

	const userInitials = `${user?.firstName.charAt(0)}${user?.lastName.charAt(
		0
	)}`;

	const privateAxios = usePrivateAxios();
	const switchMutation = useMutation({
		mutationFn: async (userType: string) => {
			return privateAxios.post("/auth/switch_user_type", { userType });
		},
		onSuccess: (response) => {
			if (response.data.data) {
				updateUser({
					...response.data.data
				});
				navigate({ to: `/${response.data.data.userType.toLowerCase()}/home` });
			}
		}

	});
	const handleSwitch = async () => {
		const userType = user?.userType === "BUYER" ? "VENDOR" : "BUYER";
		switchMutation.mutate(userType);
	};
	if (!user)
		return null;

	const contents = SWITCHER_CONSTANTS[user.userType];

	return (
		<Dialog>
			<DialogTrigger asChild>
				<SidebarMenuButton size="sm">
					<RotateCcw />
					<span className="text-base font-thin">{contents?.title}</span>
				</SidebarMenuButton>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[400px] bg-black/75 border-black">
				<div className="grid gap-4 py-4 place-items-center">
					<Avatar className="size-28 rounded-lg">
						<AvatarImage src="/change" alt="user?.fullName" />
						<AvatarFallback className="rounded-full font-bold text-xl">
							{userInitials}
						</AvatarFallback>
					</Avatar>
					<DialogTitle className="text-center text-white">
						Hi,
						{` ${user?.firstName} `}
						you are about to
						<br />
						<span className="lowercase">{` ${contents.title} `}</span>
						app
					</DialogTitle>
					<DialogDescription className="w-full text-sm text-center">
						<span className="w-[40ch] ">{contents.description}</span>
					</DialogDescription>
				</div>
				<DialogFooter>
					<div className="flex flex-col w-full gap-2">
						{switchMutation.isError
						&& <div className="w-full text-center text-red-500">Something Went Wrong!!!</div>}

						<Button onClick={handleSwitch} className="w-full" disabled={switchMutation.isPending} type="submit">
							{switchMutation.isPending
								? (
										<>
											<Loader2 size={20} className="animate-spin" />
											{" "}
											&nbsp; Loading....
										</>
									)
								: (
										` ${contents.title} `
									)}

						</Button>

						<DialogClose disabled={switchMutation.isPending} asChild>
							<Button className="w-full text-white" variant="ghost">
								Keep using
								{" "}
								{user.userType === "BUYER" ? "buyer" : "vendor"}
								{" "}
								app
							</Button>
						</DialogClose>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
